// File: app/src/utils/planNormaliser.js
//
// Light-touch normaliser applied whenever a workout plan enters React state —
// from LLM generation, from localStorage hydration, from a saved programme
// fetch, or from any user-pasted JSON. Pure function, deep-clones, never
// mutates the input.
//
// Coercions today:
//   • `SetRest` → `Rest` — legacy type the runtime already treats as Rest.
//   • Auto-injected `Rest` → `Transition` — for nodes that the transition
//     injector emitted in older plans (label-prefixed "Transition"/"Setup",
//     or short autoStart rests). Lets the renderer drive hide-from-summary
//     from `type` instead of label sniffing.
//   • Logistical (ghost) `Rest` → `Transition` — a `Rest` wedged MID-SEQUENCE
//     between two DIFFERENT exercises (the deep work-leaf either side differs).
//     These are the "rest after every set" ghosts: logistical movement, not
//     deliberate recovery. Demoted to a hidden micro-transition regardless of
//     duration or modality, and applied to ALL plans (incl. those marked
//     `recoveryModel:"explicit"`) since the prompt alone doesn't reliably stop
//     the LLM over-authoring. PRESERVED as Rest: same-exercise inter-set rests
//     (keys equal), round-boundary rests (last child of a multi-round Group),
//     the residual rest of an EMOM window (issue #63), and the zero-rest
//     superset marker (`value:0`).
//   • Double-encoded sets → loop count reset to 1 — a single-exercise Group
//     whose `value` > 1 ALSO enumerates each set as its own Work child (e.g. a
//     value=4 Group holding Bench Press 8/6/5/5), so the loop multiplies the
//     written-out volume (4 × 4 = 16 sets). Only the unambiguous ramp/pyramid
//     (reps or load vary between the enumerated sets) is collapsed; circuits,
//     correct uniform sets, and identical-set clusters are left untouched.
//   • Enumerated sets → compact sets-Group — a run of ≥2 consecutive Work
//     leaves of the SAME exercise (e.g. Bench Press 8/6/5/5 as four siblings,
//     optionally separated by inter-set rests) is folded into one
//     `Group(unit="sets", value=N)` + single Work + `setScheme` (issue #29).
//     Varying numeric dimensions (value/weight/intensityPct) become per-set
//     arrays; uniform ones stay static. Volume is preserved exactly, and
//     circuits/supersets are untouched (their runs span DIFFERENT exercises).
//   • Leaf holding children → flattened — only a container type (Group /
//     Circuit) may hold `children`. A step authored INSIDE the previous step
//     rather than beside it is silently destructive: the compiler descends into
//     any node with children but only emits leaves, so every prescription above
//     the deepest one is discarded. A descendant restating the same exercise is
//     dropped as a duplicate; anything else is hoisted to a sibling so it still
//     runs.
//   • Missing `id` → fresh UUID — every node needs a stable ID for React
//     keys and dnd-kit SortableContext. Existing IDs are preserved.
//
// Note on duration: a demoted ghost is capped at GHOST_REST_TRANSITION_SEC and
// marked autoStart so it is indistinguishable from an injected micro-transition
// (hidden in summary, non-executable in the coach, folded into total-time as a
// prediction). Without the cap a >= 60s demoted rest would classify as a MACRO
// transition — a visible divider AND a live coach phase — re-introducing the
// exact leakage this removes.

import {
  isWorkLike,
  isContainerType,
  getExerciseKey,
  getFirstWorkLeaf,
  getLastWorkLeaf,
} from "./planNodeUtils";
import { isRoundBoundaryRest, isProtocolWindowGroup } from "./planVisuals";

const SHORT_REST_THRESHOLD_SEC = 60;
// Cap for a demoted ghost rest. Matches the transition injector's default so
// demoted ghosts behave identically to injected inter-exercise transitions.
const GHOST_REST_TRANSITION_SEC = 15;

const nodeDurationSec = (node) => {
  const v = Number(node?.value);
  if (!Number.isFinite(v) || v <= 0) return 0;
  if (node?.unit === "m") return v * 60;
  if (node?.unit === "s") return v;
  return 0;
};

const isMissingId = (node) => {
  if (!node || typeof node !== "object") return false;
  return node.id == null || node.id === "";
};

// Stable ID generator: prefer crypto.randomUUID() where available, fall back
// to a counter-suffixed timestamp for older environments / test runners.
let _idCounter = 0;
const genId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  _idCounter += 1;
  return `node-${Date.now().toString(36)}-${_idCounter}`;
};

// A Rest inside an EMOM/E2MOM/E3MOM block is the residual of that window — the
// format's own prescribed recovery, however short. Demoting it to a hidden
// micro-transition would delete the rest phase from the coach and shorten the
// block below its stated duration (issue #63).
const isProtocolWindowRest = (node, parent) =>
  String(node?.type || "").toLowerCase() === "rest" &&
  isProtocolWindowGroup(parent);

// `parent`/`index` (when supplied) locate the node among its siblings so the
// short-autoStart heuristic can spare a genuine round-boundary rest (issue #37).
const isTransitionLikeLegacy = (node, parent, index) => {
  if (!node || typeof node !== "object") return false;
  if (node.type !== "Rest") return false;
  if (Number(node.value) === 0) return false; // zero-rest marker stays Rest
  const label = String(node.label || "").trim().toLowerCase();
  if (label.startsWith("transition") || label.startsWith("setup")) return true;
  // A short autoStart Rest that sits at a round boundary (last child of a
  // multi-round Group) is a deliberate intra-round recovery — e.g. the 10s
  // rest between Tabata rounds — NOT a legacy auto-injected micro-transition.
  // Preserve it so it stays visible in the plan tree (issue #37).
  if (isRoundBoundaryRest(node, parent, index)) return false;
  // Same for the residual rest of an EMOM window, which the round-boundary
  // rule misses on a single-window block (issue #63).
  if (isProtocolWindowRest(node, parent)) return false;
  if (node.autoStart === true && nodeDurationSec(node) < SHORT_REST_THRESHOLD_SEC) {
    return true;
  }
  return false;
};

// Logistical "ghost" rest: a `Rest` sitting MID-SEQUENCE between two DIFFERENT
// exercises. `prev`/`next` are the ORIGINAL immediate siblings; `parent`/`index`
// locate the node for round-boundary detection. PRESERVED (returns false):
//   • zero-rest superset marker (`value:0`),
//   • round-boundary rests (last child of a multi-round Group),
//   • any rest not flanked by two work-like siblings (e.g. trailing rest),
//   • same-exercise inter-set rests (the bordering work-leaf keys match).
// Demotes regardless of duration/modality — different bordering exercises mean
// the rest is logistical movement, not deliberate recovery.
const isGhostRest = (node, prev, next, parent, index) => {
  if (!node || node.type !== "Rest") return false;
  if (Number(node.value) <= 0) return false; // zero-rest marker stays Rest
  if (isRoundBoundaryRest(node, parent, index)) return false; // round boundary stays
  if (isProtocolWindowRest(node, parent)) return false; // EMOM residual stays
  if (!isWorkLike(prev) || !isWorkLike(next)) return false;
  const lhs = getExerciseKey(getLastWorkLeaf(prev));
  const rhs = getExerciseKey(getFirstWorkLeaf(next));
  if (!lhs || !rhs) return false;
  return lhs !== rhs; // different exercises → logistical ghost → demote
};

// Build the demoted Transition from a ghost Rest: cap duration so it stays a
// hidden micro-transition (< SHORT_REST_THRESHOLD_SEC), relabel unless already
// transition-labelled, and set autoStart to match injected transitions.
const demoteGhostRest = (node) => {
  const label = String(node.label || "").trim().toLowerCase();
  const keepLabel = label.startsWith("transition") || label.startsWith("setup");
  return {
    ...node,
    type: "Transition",
    label: keepLabel ? node.label : "Transition",
    value: Math.min(nodeDurationSec(node), GHOST_REST_TRANSITION_SEC),
    unit: "s",
    autoStart: true,
  };
};

// Double-encoded sets: a single-exercise Group whose loop count (value > 1) ALSO
// spells out the individual sets as separate Work children — e.g. a value=4 Group
// holding Bench Press 8/6/5/5, where the loop then multiplies the written-out
// sets (4 rounds × 4 sets = 16). Scoped tightly because this normaliser also runs
// on user-pasted/hydrated plans, so it fires only on the UNAMBIGUOUS ramp/pyramid
// where the enumerated sets differ in reps or load. Excluded:
//   • circuits / supersets — their Work children resolve to DIFFERENT exercises,
//   • correct uniform sets — a single Work child repeated by the loop,
//   • nested set-groups — a movement child that is itself a Group,
//   • deliberate clusters — identical mini-sets (no rep/load variation).
const isDoubleEncodedSetGroup = (node) => {
  if (!node || node.type !== "Group" || !Array.isArray(node.children)) {
    return false;
  }
  // A time-capped container's value is a DURATION, not a loop count (AMRAP cap,
  // EMOM window). Collapsing it to 1 would turn a 12-minute cap into one second.
  if (["s", "m"].includes(node.unit)) return false;
  if (Number(node.value) <= 1) return false;
  const movementKids = node.children.filter(isWorkLike);
  const workKids = node.children.filter((c) => c && c.type === "Work");
  // Every movement child must be a flat Work leaf (no nested Groups), with at
  // least two of them to constitute "enumerated sets".
  if (workKids.length < 2 || workKids.length !== movementKids.length) {
    return false;
  }
  const key = getExerciseKey(workKids[0]);
  if (!key || !workKids.every((w) => getExerciseKey(w) === key)) return false;
  // Ramp/pyramid signal: the enumerated sets aren't all identical (reps/load).
  const signature = (w) =>
    `${w.value}|${w.unit}|${w.weight ?? w.intensityPct ?? ""}`;
  return new Set(workKids.map(signature)).size > 1;
};

// --- Enumerated-set consolidation (issue #29) -------------------------------
//
// Models still sometimes spell out a multi-set exercise as N consecutive Work
// nodes (e.g. Bench Press 8/6/5/5 as four siblings) instead of the compact
// `Group(unit="sets", value=N)` + single Work + `setScheme` form. This pass
// folds such runs back into the compact representation, attaching a `setScheme`
// for whichever numeric dimension(s) vary across the sets. Volume is preserved
// exactly (N works in → a value=N sets-Group out). Circuits/supersets are left
// untouched because their consecutive Work children resolve to DIFFERENT
// exercise keys, so no same-key run forms.

// Numeric per-set dimensions a setScheme may carry. Mirrors the compiler.
const SET_SCHEME_DIMENSIONS = ["value", "weight", "intensityPct"];

// A flat Work leaf eligible to join a set run: typed Work, no children, a
// resolvable exercise key, and a finite primary value.
const isConsolidatableWork = (n) =>
  !!n &&
  n.type === "Work" &&
  (!Array.isArray(n.children) || n.children.length === 0) &&
  !!getExerciseKey(n) &&
  Number.isFinite(Number(n.value));

// An inter-set rest separating two sets of the SAME exercise (a real Rest with
// positive duration). Ghost/inter-exercise rests are already demoted to
// Transition before this runs, so they won't match here.
const isInterSetRest = (n) => !!n && n.type === "Rest" && Number(n.value) > 0;

// Do two consolidatable works belong to the same set run? Same exercise key AND
// same unit (don't merge a reps set with a timed set).
const sameSet = (a, b) =>
  isConsolidatableWork(b) &&
  getExerciseKey(a) === getExerciseKey(b) &&
  a.unit === b.unit;

// Cheap, side-effect-free detector: does this Group's children contain a run of
// ≥2 consecutive same-exercise works (optionally separated by an inter-set
// rest)? Used by hasLegacyType to decide whether a coercion pass is needed.
const hasConsolidatableRun = (node) => {
  if (!node || node.type !== "Group" || !Array.isArray(node.children)) {
    return false;
  }
  // Only flatten runs inside a single-pass container. A multi-round Group
  // (value > 1) is ambiguous — its repeated same-exercise children may be a
  // deliberate cluster (4 rounds of 2 mini-sets), so collapsing would change
  // the volume. The double-encoded case is handled separately: its value is
  // reset to 1 first, so it reaches consolidation through this gate.
  if (Number(node.value || 1) > 1) return false;
  const kids = node.children;
  for (let i = 0; i + 1 < kids.length; i++) {
    const a = kids[i];
    if (!isConsolidatableWork(a)) continue;
    if (sameSet(a, kids[i + 1])) return true;
    if (
      isInterSetRest(kids[i + 1]) &&
      kids[i + 2] != null &&
      sameSet(a, kids[i + 2])
    ) {
      return true;
    }
  }
  return false;
};

// Build the compact sets-Group from a detected run of same-exercise works plus
// the inter-set rests found between them. A setScheme dimension is emitted only
// when every set carries a finite value for it AND those values vary; a uniform
// dimension stays on the static set-1 field.
const buildSetGroup = (works, rests) => {
  const setScheme = {};
  for (const dim of SET_SCHEME_DIMENSIONS) {
    const vals = works.map((w) => w[dim]);
    if (!vals.every((v) => Number.isFinite(Number(v)))) continue;
    const nums = vals.map(Number);
    if (new Set(nums).size > 1) setScheme[dim] = nums;
  }
  const workChild = { ...works[0] }; // set 1 supplies the static fields
  delete workChild.setScheme;
  if (Object.keys(setScheme).length > 0) workChild.setScheme = setScheme;

  const children = [workChild];
  if (rests.length > 0) children.push({ ...rests[0] }); // canonical inter-set rest

  return {
    id: genId(),
    type: "Group",
    label: works[0].label,
    unit: "sets",
    value: works.length,
    children,
  };
};

// Replace each maximal run of consecutive same-exercise works (optionally
// separated by inter-set rests) with one compact sets-Group. A trailing rest
// that follows the final work of a run is NOT absorbed — it belongs to the
// parent (round boundary / changeover) and is left in place.
const consolidateSetRuns = (children) => {
  if (!Array.isArray(children) || children.length < 2) return children;
  const out = [];
  // Returned by reference when nothing folded: the repair log upstream reads
  // "did this pass change anything?" off object identity, and a fresh array
  // that happens to hold the same nodes would read as a repair that fired.
  let folded = false;
  let i = 0;
  while (i < children.length) {
    const start = children[i];
    if (!isConsolidatableWork(start)) {
      out.push(start);
      i += 1;
      continue;
    }
    const works = [start];
    const rests = [];
    let j = i + 1;
    while (j < children.length) {
      const cur = children[j];
      if (sameSet(start, cur)) {
        works.push(cur);
        j += 1;
        continue;
      }
      if (isInterSetRest(cur) && sameSet(start, children[j + 1])) {
        rests.push(cur);
        j += 1;
        continue;
      }
      break;
    }
    if (works.length >= 2) {
      out.push(buildSetGroup(works, rests));
      folded = true;
      i = j;
    } else {
      out.push(start);
      i += 1;
    }
  }
  return folded ? out : children;
};

// --- Leaf nodes holding children (schema violation) ------------------------
//
// Only a container type may hold `children` (docs/schema.md). Models sometimes
// author the next step INSIDE the previous one rather than beside it, and the
// result is destructive rather than merely untidy: `workoutCompiler` descends
// into anything with children but only EMITS leaves, so every prescription
// above the deepest child is silently discarded. A Group(sets=4) wrapping
// Back Squat → Back Squat → Back Squat compiles to four sets of the innermost
// node and drops the two above it, while the plan view still draws all three.
//
// Three repairs, none of which loses prescribed work:
//   • a node with NO prescription of its own was only ever a container — retype
//     it to a single-pass Group so its label and its contents both survive,
//   • a child that merely RESTATES its parent (same exercise, unit and value)
//     is a duplicate — drop it and keep the outer node, whose figures the plan
//     view and the timing scaler already read,
//   • anything else is a real step in the wrong place — hoist it to a sibling
//     immediately after its parent, in order, so it still runs.

const hasChildren = (node) =>
  Array.isArray(node?.children) && node.children.length > 0;

const isLeafWithChildren = (node) =>
  !!node &&
  typeof node === "object" &&
  !isContainerType(node.type) &&
  hasChildren(node);

// Does this node PRESCRIBE anything itself (a duration, a rep/calorie count)?
// A leaf without one is a label with no work in it.
const hasPrescription = (node) => Number.isFinite(Number(node?.value));

// A child that says exactly what the node it sits inside already says. NaN
// never equals NaN, so a missing value on either side is never a restatement.
const isRestatement = (outer, inner) =>
  outer.type === inner?.type &&
  !!getExerciseKey(outer) &&
  getExerciseKey(outer) === getExerciseKey(inner) &&
  outer.unit === inner.unit &&
  Number(outer.value) === Number(inner.value);

// Repair one leaf-with-children into the node sequence it should have been.
// Returns an array because the fix for a misplaced step is to make it a
// SIBLING. Recursive, so a chain nested N deep flattens in one pass.
const flattenLeafNode = (node) => {
  const kids = (node.children || []).flatMap((kid) =>
    isLeafWithChildren(kid) ? flattenLeafNode(kid) : [kid],
  );
  const flat = { ...node };
  delete flat.children;

  // Container in all but name: keep the label, give it a type that can hold
  // children and a loop count that repeats them exactly once.
  if (!hasPrescription(flat)) {
    if (kids.length === 0) return [];
    return [{ ...flat, type: "Group", unit: "rounds", value: 1, children: kids }];
  }

  return [flat, ...kids.filter((kid) => !isRestatement(flat, kid))];
};

// Does any child of this node need flattening? Mirrors exactly what the pass
// below can repair — the repair rewrites a node's CHILDREN, so a malformed
// root is left for validatePlan to report.
const hasFlattenableChild = (node) =>
  Array.isArray(node?.children) && node.children.some(isLeafWithChildren);

const flattenNode = (node) => {
  if (!node || typeof node !== "object" || !Array.isArray(node.children)) {
    return node;
  }
  return {
    ...node,
    children: node.children
      .map(flattenNode)
      .flatMap((child) =>
        isLeafWithChildren(child) ? flattenLeafNode(child) : [child],
      ),
  };
};

/**
 * Hoist children off leaf-type nodes so only containers hold them.
 *
 * Runs on its own (early in the generation pipeline, so transition injection,
 * set consolidation and the timing scaler all see a schema-valid tree) and
 * again inside `coerceLegacyNodeTypes` for plans arriving from anywhere else.
 * Idempotent, and returns the input by reference when there is nothing to fix.
 *
 * @param {object|null} plan Workout plan root (or null).
 * @param {object}  [opts]
 * @param {Function} [opts.onRepair] Called with a repair code when this fires.
 * @returns {object|null} Plan with every leaf type childless.
 */
export const flattenLeafContainers = (plan, { onRepair } = {}) => {
  if (!plan || typeof plan !== "object") return plan;
  const needsFlattening = (node) =>
    hasFlattenableChild(node) ||
    (Array.isArray(node?.children) && node.children.some(needsFlattening));
  if (!needsFlattening(plan)) return plan;
  if (onRepair) onRepair("repair.leafFlatten");
  return flattenNode(plan);
};

// A `setScheme` that actually prescribes something — at least one dimension
// holding a non-empty array. An empty `{}` is inert and left where it lies.
const isPopulatedSetScheme = (scheme) =>
  !!scheme &&
  typeof scheme === "object" &&
  !Array.isArray(scheme) &&
  Object.values(scheme).some((arr) => Array.isArray(arr) && arr.length > 0);

// A `setScheme` authored on the sets-Group rather than on the Work it describes
// (issue #74). The compiler only resolves per-set values on leaves, so a scheme
// up here was silently ignored and the block ran set 1's figures for every set.
// Only liftable when the Group holds exactly one Work child that has no scheme
// of its own — any other shape is a guess about which movement ramps.
const liftableSetScheme = (node) => {
  if (node?.type !== "Group" || !isPopulatedSetScheme(node.setScheme)) {
    return null;
  }
  const works = (node.children || []).filter((c) => c?.type === "Work");
  if (works.length !== 1 || isPopulatedSetScheme(works[0].setScheme)) {
    return null;
  }
  return works[0];
};

const hasLegacyType = (node, parent, index) => {
  if (!node || typeof node !== "object") return false;
  if (node.type === "SetRest") return true;
  if (hasFlattenableChild(node)) return true;
  if (isTransitionLikeLegacy(node, parent, index)) return true;
  if (isMissingId(node)) return true;
  if (isDoubleEncodedSetGroup(node)) return true;
  if (hasConsolidatableRun(node)) return true;
  if (liftableSetScheme(node)) return true;
  if (Array.isArray(node.children)) {
    const kids = node.children;
    return kids.some((child, i) => {
      if (
        !isTransitionLikeLegacy(child, node, i) &&
        isGhostRest(child, kids[i - 1], kids[i + 1], node, i)
      ) {
        return true;
      }
      return hasLegacyType(child, node, i);
    });
  }
  return false;
};

// --- Set-volume normalisation ----------------------------------------------
//
// Five repairs that all answer ONE question — how many times does this exercise
// actually run? — and they are order-dependent, each feeding the next:
//
//   1. flattenChildren       a step nested inside another step becomes a
//                            sibling, so the steps below see a real sequence
//                            rather than a chain
//   2. lowerSetScheme        a per-set array authored on the Group drops onto
//                            the Work the compiler resolves it against
//   3. unwindDoubleEncoding  a Group that BOTH enumerates every set AND repeats
//                            has its loop count reset, so 4 sets stop meaning 16
//   4. consolidateRuns       a run of same-exercise siblings folds into one
//                            compact sets-Group + setScheme; gated to
//                            single-pass containers, which step 3 is what makes
//                            reachable
//   5. hoistWrapper          the single-exercise wrapper steps 3-4 leave behind
//                            is dropped, keeping its id
//
// Every step preserves prescribed volume exactly — that is the invariant worth
// protecting here, because getting it wrong silently doubles someone's training
// load. The sequence used to be five separate blocks inside coerceNode, each
// with a comment explaining what it assumed the others had already done; stated
// once, in order, it is one thing to reason about and a new repair has an
// obvious place to go.

// 1. See flattenLeafContainers for the three repairs and why they are safe.
const flattenChildren = (node) => {
  if (!hasFlattenableChild(node)) return node;
  return {
    ...node,
    children: node.children.flatMap((child) =>
      isLeafWithChildren(child) ? flattenLeafNode(child) : [child],
    ),
  };
};

// 2. Issue #74: the compiler resolves per-set values on leaves only, so a
//    scheme left on the Group ran set 1's figures for every set.
const lowerSetScheme = (node) => {
  const target = liftableSetScheme(node);
  if (!target) return node;
  const next = {
    ...node,
    children: node.children.map((child) =>
      child === target ? { ...child, setScheme: node.setScheme } : child,
    ),
  };
  delete next.setScheme;
  return next;
};

// 3. Only the unambiguous ramp/pyramid — see isDoubleEncodedSetGroup.
const unwindDoubleEncoding = (node) =>
  isDoubleEncodedSetGroup(node) ? { ...node, value: 1 } : node;

// 4. Issue #29. A deliberate multi-round cluster keeps its rounds untouched.
const consolidateRuns = (node) => {
  if (node.type !== "Group" || Number(node.value || 1) > 1) return node;
  if (!Array.isArray(node.children)) return node;
  const children = consolidateSetRuns(node.children);
  return children === node.children ? node : { ...node, children };
};

// 5. Fires only when the wrapper names nothing of its own — either anonymous or
//    already the same exercise as the group inside it — so phase blocks
//    (Warm-up / Main / Cool-down) are never collapsed into their contents.
const hoistWrapper = (node) => {
  const inner = Array.isArray(node.children) ? node.children[0] : null;
  if (
    node.type !== "Group" ||
    node.id === "root" ||
    String(node.unit || "").toLowerCase() === "workout" ||
    Number(node.value || 1) > 1 ||
    node.children.length !== 1 ||
    inner?.type !== "Group" ||
    String(inner?.unit || "").toLowerCase() !== "sets" ||
    (getExerciseKey(node) && getExerciseKey(node) !== getExerciseKey(inner))
  ) {
    return node;
  }
  return { ...inner, id: node.id };
};

// Each step is paired with the code it reports. Every step returns its input
// BY REFERENCE when it has nothing to do, so "did this repair fire?" is just an
// identity check — no step needs to know it is being counted.
const SET_VOLUME_PIPELINE = [
  ["repair.leafFlatten", flattenChildren],
  ["repair.setSchemeLower", lowerSetScheme],
  ["repair.doubleEncodedSets", unwindDoubleEncoding],
  ["repair.enumeratedSets", consolidateRuns],
  ["repair.wrapperHoist", hoistWrapper],
];

const normaliseSetVolume = (node, report) =>
  SET_VOLUME_PIPELINE.reduce((current, [code, step]) => {
    const next = step(current);
    if (next !== current) report(code);
    return next;
  }, node);

const coerceNode = (node, parent, index, report) => {
  if (!node || typeof node !== "object") return node;
  const next = { ...node };
  if (isTransitionLikeLegacy(next, parent, index)) {
    next.type = "Transition";
    report("repair.transitionLike");
  } else if (next.type === "SetRest") {
    next.type = "Rest";
    report("repair.setRest");
  }
  if (isMissingId(next)) {
    next.id = genId();
    report("repair.missingId");
  }
  if (Array.isArray(next.children)) {
    const kids = next.children;
    next.children = kids.map((child, i) => {
      const coerced = coerceNode(child, node, i, report);
      // Structural ghost demotion. Evaluate against the ORIGINAL siblings
      // (kids[i±1]) and original parent (node), only when the per-node coercion
      // above didn't already turn this into a Transition.
      if (
        coerced.type === "Rest" &&
        isGhostRest(child, kids[i - 1], kids[i + 1], node, i)
      ) {
        report("repair.ghostRest");
        return demoteGhostRest(coerced);
      }
      return coerced;
    });
  }

  // Everything about how many times an exercise runs, in one ordered pass.
  // Runs on the already-coerced children so ghost rests are demoted and only
  // genuine inter-set rests remain for the consolidator to absorb.
  return normaliseSetVolume(next, report);
};

/**
 * Coerce node types on any plan tree:
 *   • leaf holding children → flattened (see flattenLeafContainers)
 *   • SetRest → Rest
 *   • auto-injected Rest → Transition (label/autoStart heuristics)
 *   • logistical (ghost) Rest → Transition — a mid-sequence rest between two
 *     DIFFERENT exercises, demoted on ALL plans (incl. `recoveryModel:"explicit"`)
 *     and capped to a hidden micro-transition. Same-exercise inter-set rests,
 *     round-boundary rests, and the zero-rest marker are preserved.
 *   • double-encoded set group → loop count reset to 1 — a single-exercise
 *     Group that both repeats (value > 1) AND enumerates each set as its own
 *     Work child (ramp/pyramid only), which would otherwise multiply the volume.
 *
 * Returns the input unchanged when no coercion is needed (preserves reference
 * equality for memoisation); otherwise returns a new deep-cloned tree so
 * persisted state stays untouched until the caller writes it back.
 *
 * Pass `onRepair` to find out WHICH repairs fired — it receives a
 * `repair.<name>` code once per application, which is how the generation
 * pipeline builds the per-code counts that say whether a pass still earns its
 * place. Omit it and nothing is counted.
 *
 * @param {object|null} plan Workout plan root (or null).
 * @param {object}  [opts]
 * @param {Function} [opts.onRepair] Called with a repair code per application.
 * @returns {object|null} Normalised plan, or null if input was falsy.
 */
export const coerceLegacyNodeTypes = (plan, { onRepair } = {}) => {
  if (!plan) return plan;
  if (!hasLegacyType(plan)) return plan;
  const report = onRepair || (() => {});
  return coerceNode(plan, undefined, undefined, report);
};

export default coerceLegacyNodeTypes;
